import { ChipProps as CoreChipProps, Chip as CoreChip } from "./internal/Chip";
import { Icon as CoreIcon, IconProps as CoreIconProps } from "../Icon";
import { AvatarProps as CoreAvatarProps } from "../Avatar/internal/Avatar";

import { forwardRef, type Ref } from "react";
import { useTrackingId } from "../../hooks";
import { DataTrackingId, DistributiveOmit } from "../../types";
import { AiMarkBooleanProps } from "../../types/ai-marks";
import { AiMark } from "../AiMark/AiMark";

/**
 * Props for the Chip component
 * @property {string} label - The text that displays in the chip
 * @property {"small" | "medium"} [size] - The size of the chip
 * @property {string} [color] - Color of the Chip
 * @property {boolean} [aiMark] - Whether to display an AI mark icon in the chip
 * @property {(e?: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void} [onClick] - Callback for when the Chip is clicked
 * @property {(e: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>) => void} [onClose] - Called when the component is closed
 * @property {boolean} [textWrap] - Wraps text when it overflows
 * @property {Svg} [icon] - Icon displayed in the chip, before the text.
 * @property {string} [avatar] - Image URL to display in an avatar before the chip text.
 * @extends CoreChipProps
 * @extends DataTrackingId
 * @extends AiMarkBooleanProps
 */
export type ChipProps = DistributiveOmit<
  CoreChipProps,
  "prefix" | "avatar" | "suffix"
> &
  DataTrackingId &
  AiMarkBooleanProps &
  (
    | { icon?: CoreIconProps["svg"]; avatar?: never }
    | { icon?: never; avatar?: CoreAvatarProps["image"] }
  );

/**
 * Chip component for displaying compact information or tags.
 *
 * Features:
 * - Configurable sizes (small, medium)
 * - Custom color support with automatic contrast calculation
 * - Optional click handler for interactive chips
 * - Optional close button for removable chips
 * - Optional AI mark icon to indicate AI-generated content
 * - Automatic AI mark type selection: gradient on default chips, inherited color on colored chips
 * - AI mark on clickable chips (hover/focus morph temporarily disabled; see @TODO in source)
 * - Text wrapping support for long labels
 * - Full accessibility support with ARIA attributes
 * - Keyboard navigation (Enter/Space to click, Delete/Backspace to close)
 * - Screen reader instructions for removal actions
 * - Theme-aware color adaptation
 * - Layout utility props for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Chip
 *   label="JavaScript"
 *   color="#f7df1e"
 *   onClose={(e) => console.log('Chip removed')}
 *   onClick={(e) => console.log('Chip clicked')}
 * />
 *
 * @example
 * <Chip label="AI Generated" aiMark />
 */
export const Chip = forwardRef(
  (
    {
      icon,
      aiMark,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      ...props
    }: ChipProps,
    ref: Ref<HTMLSpanElement>,
  ) => {
    const data = {
      label: props.label,
      size: props.size,
      color: props.color,
    };

    const trackingId = useTrackingId({
      name: "Chip",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const prefix =
      icon != null ? (
        <CoreIcon svg={icon} color="var(--int-font-color)" size={props.size} />
      ) : null;

    const aiMarkType = props.color ? "default" : "gradient";

    // @TODO Re-enable AI mark hover/focus morph for `aiMark` + `onClick` chips after publish.
    // Disabled: composed animation misaligned vs static asset / layout shift (see Chip AI Mark Fix 1 stash).
    const suffix = aiMark ? (
      <AiMark
        type={aiMarkType}
        size="small"
        color={props.color ? "var(--int-font-color)" : undefined}
      />
    ) : undefined;

    const interactionProps = {
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
    };

    return (
      <CoreChip
        ref={ref}
        data-tracking-id={trackingId}
        {...props}
        {...interactionProps}
        prefix={prefix}
        suffix={suffix}
      />
    );
  },
);
Chip.displayName = CoreChip.displayName;
